import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { clientGroupsAPI } from '../services/api';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Tooltip,
  Grid,
  Checkbox,
  Divider,
  useTheme,
  useMediaQuery,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Block as DisableIcon,
  CheckCircle as EnableIcon,
  AccessTime as TimeIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
} from '@mui/icons-material';

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const ClientGroups = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    minOrderDaysAdvance: 2,
    schedule: {},
    orderSchedule: {},
    enabled: true,
  });
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const response = await clientGroupsAPI.getAll();
      setGroups(response.data);
      setError('');
    } catch (err) {
      setError(t('clientGroups.failedToLoad'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const getDefaultSchedule = () => {
    const schedule = {};
    DAY_KEYS.forEach((key) => {
      schedule[key] = { enabled: false };
    });
    return schedule;
  };

  // Order-taking window default: every day open, no time ranges (= unrestricted)
  const getDefaultOrderSchedule = () => {
    const schedule = {};
    DAY_KEYS.forEach((key) => {
      schedule[key] = { enabled: true, ranges: [] };
    });
    return schedule;
  };

  const handleOpenModal = (group = null) => {
    if (group) {
      setEditingGroup(group);
      setFormData({
        code: group.code || '',
        name: group.name,
        description: group.description || '',
        minOrderDaysAdvance: group.minOrderDaysAdvance ?? 2,
        schedule: group.schedule || getDefaultSchedule(),
        orderSchedule: group.orderSchedule || getDefaultOrderSchedule(),
        enabled: group.enabled,
      });
    } else {
      setEditingGroup(null);
      setFormData({
        code: '', name: '', description: '', minOrderDaysAdvance: 2,
        schedule: getDefaultSchedule(), orderSchedule: getDefaultOrderSchedule(), enabled: true,
      });
    }
    setModalOpen(true);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await clientGroupsAPI.exportCsv();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `grupos-${new Date().toISOString().slice(0,10)}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError(t('clientGroups.failedToExport'));
    } finally {
      setExporting(false);
    }
  };

  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        setImporting(true);
        const response = await clientGroupsAPI.importCsv(ev.target.result);
        setImportResult(response.data);
        fetchGroups();
      } catch (err) {
        setError(err.response?.data?.message || t('clientGroups.failedToImport'));
      } finally {
        setImporting(false);
        e.target.value = '';
      }
    };
    reader.readAsText(file);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingGroup(null);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'enabled' ? checked : type === 'number' ? parseInt(value) || 0 : value,
    });
  };

  const handleDayToggle = (dayKey) => {
    setFormData({
      ...formData,
      schedule: {
        ...formData.schedule,
        [dayKey]: { enabled: !formData.schedule[dayKey]?.enabled },
      },
    });
  };

  // ── Order-taking window (orderSchedule) handlers ──────────────────────────────
  const setOrderDay = (dayKey, patch) => {
    setFormData((prev) => ({
      ...prev,
      orderSchedule: {
        ...prev.orderSchedule,
        [dayKey]: { ...prev.orderSchedule[dayKey], ...patch },
      },
    }));
  };

  const handleOrderDayToggle = (dayKey) => {
    const day = formData.orderSchedule[dayKey] || {};
    setOrderDay(dayKey, { enabled: !day.enabled, ranges: day.ranges || [] });
  };

  const handleOrderTimeChange = (dayKey, index, field, value) => {
    const ranges = [...(formData.orderSchedule[dayKey]?.ranges || [])];
    ranges[index] = { ...ranges[index], [field]: value };
    setOrderDay(dayKey, { ranges });
  };

  const addOrderRange = (dayKey) => {
    const ranges = [...(formData.orderSchedule[dayKey]?.ranges || []), { start: '08:00', end: '18:00' }];
    setOrderDay(dayKey, { ranges });
  };

  const removeOrderRange = (dayKey, index) => {
    const ranges = (formData.orderSchedule[dayKey]?.ranges || []).filter((_, i) => i !== index);
    setOrderDay(dayKey, { ranges });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingGroup) {
        await clientGroupsAPI.update(editingGroup.id, formData);
      } else {
        await clientGroupsAPI.create(formData);
      }
      handleCloseModal();
      fetchGroups();
    } catch (err) {
      setError(err.response?.data?.message || t('clientGroups.failedToSave'));
    }
  };

  const handleToggleStatus = async (group) => {
    try {
      if (group.enabled) {
        await clientGroupsAPI.disable(group.id);
      } else {
        await clientGroupsAPI.enable(group.id);
      }
      fetchGroups();
    } catch (err) {
      setError(t('clientGroups.failedToUpdateStatus'));
    }
  };

  const formatSchedule = (schedule) => {
    if (!schedule) return '-';
    const activeDays = DAY_KEYS.filter((key) => schedule[key]?.enabled);
    if (activeDays.length === 0) return t('clientGroups.noDaysConfigured');
    return activeDays.map((key) => t(`clientGroups.days.${key}`).substring(0, 3)).join(', ');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>{t('clientGroups.title')}</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button variant="outlined" startIcon={exporting ? <CircularProgress size={16} /> : <ExportIcon />}
            onClick={handleExport} disabled={exporting}>
            {exporting ? t('clientGroups.exporting') : t('clientGroups.exportCsv')}
          </Button>
          <Button variant="outlined" startIcon={importing ? <CircularProgress size={16} /> : <ImportIcon />}
            onClick={() => fileInputRef.current?.click()} disabled={importing}>
            {importing ? t('clientGroups.importing') : t('clientGroups.importCsv')}
          </Button>
          <input type="file" accept=".csv,text/csv" ref={fileInputRef}
            style={{ display: 'none' }} onChange={handleImportFile} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenModal()}>
            {t('clientGroups.addGroup')}
          </Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 600 }}>{t('clientGroups.code')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('clientGroups.name')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('clientGroups.description')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('clientGroups.activeDays')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('clientGroups.minOrderDaysAdvance')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('clientGroups.status')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('clientGroups.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group) => (
              <TableRow key={group.id}>
                <TableCell><code>{group.code}</code></TableCell>
                <TableCell>{group.name}</TableCell>
                <TableCell>{group.description}</TableCell>
                <TableCell>{formatSchedule(group.schedule)}</TableCell>
                <TableCell>{group.minOrderDaysAdvance === 0 ? '∞' : group.minOrderDaysAdvance ?? 2}</TableCell>
                <TableCell>
                  <Chip
                    label={group.enabled ? t('common.enabled') : t('common.disabled')}
                    color={group.enabled ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title={t('common.edit')}>
                    <IconButton onClick={() => handleOpenModal(group)} size="small">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={group.enabled ? t('common.disable') : t('common.enable')}>
                    <IconButton
                      onClick={() => handleToggleStatus(group)}
                      size="small"
                      color={group.enabled ? 'error' : 'success'}
                    >
                      {group.enabled ? <DisableIcon /> : <EnableIcon />}
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Import Result Dialog */}
      <Dialog open={!!importResult} onClose={() => setImportResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('clientGroups.importResults')}</DialogTitle>
        <DialogContent>
          {importResult && (
            <Box>
              <Alert severity={importResult.errors?.length ? 'warning' : 'success'} sx={{ mb: 2 }}>
                {t('clientGroups.importSummary', {
                  created: importResult.created, updated: importResult.updated,
                  errors: importResult.errors?.length ?? 0,
                })}
              </Alert>
              {importResult.errors?.length > 0 && (
                <Box component="ul" sx={{ pl: 2 }}>
                  {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportResult(null)} variant="contained">{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit / Create Modal Dialog */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth fullScreen={fullScreen}>
        <DialogTitle>
          {editingGroup ? t('clientGroups.editGroup') : t('clientGroups.addNew')}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField fullWidth label={t('clientGroups.groupCode')} name="code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              required margin="normal"
              helperText={t('clientGroups.groupCodeHint')}
              disabled={!!editingGroup}
              inputProps={{ style: { textTransform: 'uppercase', fontFamily: 'monospace' } }}
            />
            <TextField fullWidth label={t('clientGroups.groupName')} name="name"
              value={formData.name} onChange={handleChange} required margin="normal" />
            <TextField fullWidth label={t('clientGroups.description')} name="description"
              value={formData.description} onChange={handleChange} margin="normal" multiline rows={2} />
            <TextField fullWidth label={t('clientGroups.minOrderDaysAdvance')} name="minOrderDaysAdvance"
              type="number" value={formData.minOrderDaysAdvance} onChange={handleChange} margin="normal"
              inputProps={{ min: 0 }} helperText={t('clientGroups.minOrderDaysAdvanceHint')} />

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              <TimeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {t('clientGroups.deliveryDays')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('clientGroups.deliveryDaysHint')}
            </Typography>

            <Grid container spacing={1}>
              {DAY_KEYS.map((dayKey) => (
                <Grid item xs={12} sm={6} key={dayKey}>
                  <Paper variant="outlined" sx={{ px: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.schedule[dayKey]?.enabled || false}
                          onChange={() => handleDayToggle(dayKey)}
                        />
                      }
                      label={t(`clientGroups.days.${dayKey}`)}
                    />
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              <TimeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {t('clientGroups.orderHours')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('clientGroups.orderHoursHint')}
            </Typography>

            <Grid container spacing={2}>
              {DAY_KEYS.map((dayKey) => {
                const day = formData.orderSchedule[dayKey] || { enabled: false, ranges: [] };
                return (
                  <Grid item xs={12} key={dayKey}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Box display="flex" alignItems="center">
                        <Checkbox
                          checked={day.enabled || false}
                          onChange={() => handleOrderDayToggle(dayKey)}
                        />
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                          {t(`clientGroups.days.${dayKey}`)}
                        </Typography>
                      </Box>

                      {day.enabled && (
                        <Box ml={4} mt={1}>
                          {(day.ranges || []).length === 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {t('clientGroups.orderHoursAllDay')}
                            </Typography>
                          )}
                          {(day.ranges || []).map((range, index) => (
                            <Box key={index} display="flex" alignItems="center" gap={2} mb={1}>
                              <TextField
                                label={t('clientGroups.start')} type="time" value={range.start || '08:00'}
                                onChange={(e) => handleOrderTimeChange(dayKey, index, 'start', e.target.value)}
                                InputLabelProps={{ shrink: true }} size="small" sx={{ width: 130 }}
                              />
                              <Typography>{t('clientGroups.to')}</Typography>
                              <TextField
                                label={t('clientGroups.end')} type="time" value={range.end || '18:00'}
                                onChange={(e) => handleOrderTimeChange(dayKey, index, 'end', e.target.value)}
                                InputLabelProps={{ shrink: true }} size="small" sx={{ width: 130 }}
                              />
                              <Button size="small" color="error" onClick={() => removeOrderRange(dayKey, index)}>
                                {t('clientGroups.remove')}
                              </Button>
                            </Box>
                          ))}
                          <Button size="small" onClick={() => addOrderRange(dayKey)} sx={{ mt: 1 }}>
                            {t('clientGroups.addTimeRange')}
                          </Button>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>

            <FormControlLabel
              control={<Switch name="enabled" checked={formData.enabled} onChange={handleChange} />}
              label={t('common.enabled')} sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained">
              {editingGroup ? t('common.update') : t('common.create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default ClientGroups;
